// src/padplanner/PadPlannerNav.jsx
//
// PadPlanner's section strip: Dashboard · Plans · Library. Until it existed the
// plans list and the library were reached only by typing the URL (from the
// dashboard there was no way to either).
//
// The mockups draw no section navigation (1-dashboard has "View all pending",
// 2-plans-list-pending a "· Dashboard" back-link), so this is the fallback DB
// set: a strip in the PadPlanner shell, styled like TopNav's modes strip,
// container and buttons alike, so it reads as the app's own secondary strip.
//
// PadPlannerGate renders it, so it is there only when the org holds PadPlanner
// and only on a page that names its section. The editor names none: it keeps
// its own header. Plan detail and the new-plan form are Plans.
//
// Its right end is a slot a page may fill (NavEnd): the plan page puts its
// lifecycle stepper there (2026-10-31). Every other page leaves it empty, and
// an empty slot takes no room, so their strip is as it was.
import { createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import { PADPLANNER_DISPLAY_NAME } from "../constants/analyticsProducts";

// The slot's element, once the strip has rendered it; null without a strip.
export const NavEndContext = createContext(null);

/** Renders its children at the strip's right end, or nothing on a page without the strip. */
export function NavEnd({ children }) {
  const el = useContext(NavEndContext);
  return el ? createPortal(children, el) : null;
}

export const PP_SECTIONS = Object.freeze([
  { key: "dashboard", label: "Dashboard", to: "/padplanner" },
  { key: "plans", label: "Plans", to: "/padplanner/plans" },
  { key: "library", label: "Library", to: "/padplanner/library" },
]);

export default function PadPlannerNav({ section, endRef = null }) {
  const navigate = useNavigate();
  return (
    <nav className="flex shrink-0 items-center gap-3 border-b border-bg-border px-4 py-1.5" aria-label={PADPLANNER_DISPLAY_NAME} data-testid="pp-nav">
      <div className="flex items-center gap-0.5 rounded-md border border-slate-700 bg-bg p-0.5">
        {PP_SECTIONS.map((s) => {
          const active = s.key === section;
          return (
            <button key={s.key} type="button" onClick={() => navigate({ to: s.to })}
              aria-current={active ? "page" : undefined}
              className={`rounded-md px-2 py-1 text-xs transition ${active
                ? "border border-accent-action/40 text-accent-action"
                : "text-gray-400 hover:text-gray-200"}`}>
              {s.label}
            </button>
          );
        })}
      </div>
      <div ref={endRef} className="ml-auto flex min-w-0 items-center" data-testid="pp-nav-end" />
    </nav>
  );
}
